/*
 * Hexlox Product Finder — engine.
 *
 * Pure logic, no DOM and no shared mutable state: every function takes its inputs and
 * returns new values; the config is never modified. The same file runs in the browser
 * (window.HexloxFinderEngine) and in Node for the tests (require).
 *
 *   const engine = HexloxFinderEngine.create(config);
 *   engine.nextQuestion(part, answers)   -> question | null (part finished)
 *   engine.resolve(part, answers, opts)  -> recommended lines + included add-ons
 *   engine.dispatch(state, action)       -> next screen state (immutable)
 *   engine.buildCart(state, opts)        -> { items, attributes } for cart/add.js + cart/update.js
 *                                           (attributes: Hexlox Code if typed, "I already own" notes for
 *                                            Key and Inserts, plus hidden _finder version)
 *
 * `answers` for one part is a plain object keyed by question ID, e.g.
 *   { "front.axle": "thru", "front.know": "yes",
 *     "front.measure": { diameter: "12", length: "142 mm", threadPitch: "1,5" } }
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.HexloxFinderEngine = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var hasOwn = function (obj, key) { return Object.prototype.hasOwnProperty.call(obj, key); };
  var clone = function (value) { return JSON.parse(JSON.stringify(value)); };

  // ---- Typed thru-axle values ------------------------------------------------------------
  // Accepts "12", "12 mm", "12mm", "1,75", "1.75". Returns the number, or null if it is not a
  // plain positive number. Parsing is only for checking: the order always gets the text as typed.
  function parseMeasurement(raw) {
    if (raw === null || raw === undefined) return null;
    var t = String(raw).trim().toLowerCase().replace(/\s*mm$/, '').replace(',', '.');
    if (!/^\d+(\.\d*)?$/.test(t)) return null;
    var n = parseFloat(t);
    return n > 0 ? n : null;
  }

  function create(config) {
    if (!config || !config.parts || !config.questions || !config.outcomes || !config.products) {
      throw new Error('Hexlox Finder: config is missing parts, questions, outcomes or products');
    }

    var partsById = {};
    config.parts.forEach(function (p) { partsById[p.id] = p; });
    var partOrder = config.parts.map(function (p) { return p.id; });
    var fields = config.measurements.fields;

    function getPart(id) {
      if (!hasOwn(partsById, id)) throw new Error('Hexlox Finder: unknown part "' + id + '"');
      return partsById[id];
    }
    function getQuestion(id) {
      if (!hasOwn(config.questions, id)) throw new Error('Hexlox Finder: unknown question "' + id + '"');
      return config.questions[id];
    }
    function getProduct(key) {
      if (!hasOwn(config.products, key)) throw new Error('Hexlox Finder: unknown product "' + key + '"');
      return config.products[key];
    }
    function toQty(value) {
      var n = Number(value);
      return Number.isInteger(n) && n > 0 ? n : 0;
    }

    // ---- Measurements --------------------------------------------------------------------
    // Returns { status: 'empty' | 'error' | 'warning' | 'ok', value, message: {text, vars} | null }.
    // 'error' blocks Continue; 'warning' is a soft hint and does not block.
    function checkMeasurement(part, fieldId, raw) {
      var text = String(raw === undefined || raw === null ? '' : raw).trim();
      if (!text) return { status: 'empty', value: null, message: null };
      var field = fields.filter(function (f) { return f.id === fieldId; })[0];
      if (!field) throw new Error('Hexlox Finder: unknown measurement field "' + fieldId + '"');
      var n = parseMeasurement(text);
      if (n === null) {
        return { status: 'error', value: null, message: { text: config.measurements.error, vars: { example: field.example } } };
      }
      var rule = config.measurements.warnings[fieldId];
      var warn = null;
      if (rule && rule.usual) {
        var usual = Array.isArray(rule.usual) ? rule.usual : rule.usual[part];
        if (usual && usual.indexOf(n) === -1) warn = typeof rule.text === 'string' ? rule.text : rule.text[part];
      }
      if (rule && typeof rule.min === 'number' && (n < rule.min || n > rule.max)) warn = rule.text;
      if (warn) return { status: 'warning', value: n, message: { text: warn, vars: {} } };
      return { status: 'ok', value: n, message: null };
    }

    function measurementsValid(part, value) {
      if (!value || typeof value !== 'object') return false;
      return fields.every(function (f) {
        var c = checkMeasurement(part, f.id, value[f.id]);
        return c.status === 'ok' || c.status === 'warning';
      });
    }

    function gridValid(question, value) {
      if (!value || typeof value !== 'object') return false;
      var rowIds = question.rows.map(function (r) { return r.id; });
      var keysOk = Object.keys(value).every(function (k) {
        var n = Number(value[k]);
        return rowIds.indexOf(k) !== -1 && Number.isInteger(n) && n >= 0;
      });
      var total = rowIds.reduce(function (sum, id) { return sum + toQty(value[id]); }, 0);
      return keysOk && total > 0;
    }

    function isValidAnswer(part, question, value) {
      if (question.type === 'choice') {
        return question.options.some(function (o) { return o.id === value; });
      }
      if (question.type === 'measurements') return measurementsValid(part, value);
      if (question.type === 'grid') return gridValid(question, value);
      return false;
    }

    // ---- Walking the questions -----------------------------------------------------------
    // Follows the answers from the part's first question. Answers to questions that are not on
    // the current path are ignored, so a changed answer can never pull in a stale branch.
    function walk(part, answers) {
      answers = answers || {};
      var qid = getPart(part).first_question;
      var visited = [];
      for (var guard = 0; guard < 50; guard++) {
        var q = getQuestion(qid);
        if (!hasOwn(answers, qid) || !isValidAnswer(part, q, answers[qid])) {
          return { visited: visited, current: qid, outcome: null, question: null };
        }
        visited.push(qid);
        if (q.type === 'choice') {
          var opt = q.options.filter(function (o) { return o.id === answers[qid]; })[0];
          if (opt.outcome) return { visited: visited, current: null, outcome: opt.outcome, question: qid };
          qid = opt.next;
        } else {
          return { visited: visited, current: null, outcome: q.outcome, question: qid };
        }
      }
      throw new Error('Hexlox Finder: question loop in part "' + part + '"');
    }

    function questionView(id) {
      return Object.assign({ id: id }, clone(getQuestion(id)));
    }

    function nextQuestion(part, answers) {
      var w = walk(part, answers);
      return w.current ? questionView(w.current) : null;
    }

    function path(part, answers) {
      var w = walk(part, answers);
      return { answered: w.visited.slice(), current: w.current, complete: !w.current };
    }

    // ---- Recommendation for one part -----------------------------------------------------
    function fillVars(ref, ctx) {
      if (ref === null || ref === undefined) return null;
      if (typeof ref === 'string') return { text: ref, vars: {} };
      if (hasOwn(ref, 'literal')) return { literal: ref.literal, vars: {} };
      var vars = {};
      Object.keys(ref.vars || {}).forEach(function (k) {
        var v = ref.vars[k];
        vars[k] = typeof v === 'string' && v.charAt(0) === '$' ? String(ctx[v.slice(1)]) : v;
      });
      return { text: ref.text, vars: vars };
    }

    function finderSpec(part, position, m) {
      var raw = function (id) { return m[id] === undefined || m[id] === null ? '' : String(m[id]); };
      return JSON.stringify({
        source: 'customer_entered',
        finder_version: config.finder_version,
        part: part,
        position: String(position).toLowerCase(),
        diameter_raw: raw('diameter'),
        diameter_mm: parseMeasurement(m.diameter),
        length_raw: raw('length'),
        length_mm: parseMeasurement(m.length),
        thread_pitch_raw: raw('threadPitch'),
        thread_pitch: parseMeasurement(m.threadPitch)
      });
    }

    function makeLine(productKey, quantity, ctx, extra) {
      var product = getProduct(productKey);
      return Object.assign({
        key: productKey,
        product: productKey,
        variantId: product.variant_id,
        sku: product.sku,
        quantity: quantity,
        properties: {},
        reasons: [],
        detail: null
      }, extra || {});
    }

    // opts.quantities: { [lineKey]: n } — quantities changed on the "Your selection" screen.
    function resolve(part, answers, opts) {
      opts = opts || {};
      var quantities = opts.quantities || {};
      var w = walk(part, answers);
      if (!w.outcome) return { complete: false, next: w.current, lines: [], addons: [] };

      var outcome = config.outcomes[w.outcome];
      if (!outcome) throw new Error('Hexlox Finder: unknown outcome "' + w.outcome + '"');
      var question = getQuestion(w.question);
      var lines;

      if (outcome.from_grid) {
        var grid = answers[w.question];
        lines = question.rows.filter(function (r) { return toQty(grid[r.id]) > 0; }).map(function (r) {
          var qty = toQty(quantities[r.product]) || toQty(grid[r.id]);
          var kind = r.non_magnetic ? 'non_magnetic' : 'magnetic';
          var ctx = { qty: qty, size: r.size };
          return makeLine(r.product, qty, ctx, {
            reasons: question.row_reasons[kind].map(function (x) { return fillVars(x, ctx); }),
            detail: fillVars(question.row_detail[kind], ctx)
          });
        });
      } else {
        var m = question.type === 'measurements' ? answers[w.question] : {};
        lines = outcome.lines.map(function (l) {
          var qty = toQty(quantities[l.product]) || l.quantity;
          var ctx = { qty: qty, finder_spec: '' };
          fields.forEach(function (f) { ctx[f.id] = m[f.id] === undefined ? '' : String(m[f.id]).trim(); });
          var props = {};
          Object.keys(l.properties || {}).forEach(function (name) {
            var v = l.properties[name];
            if (v === '$finder_spec') props[name] = finderSpec(part, l.properties.Position || part, m);
            else if (typeof v === 'string' && v.charAt(0) === '$') props[name] = ctx[v.slice(1)];
            else props[name] = v;
          });
          return makeLine(l.product, qty, ctx, {
            properties: props,
            reasons: (outcome.reasons || []).map(function (x) { return fillVars(x, ctx); }),
            detail: fillVars(outcome.detail, ctx),
            check: fillVars(outcome.check, ctx),
            note: fillVars(outcome.note, ctx)
          });
        });
      }

      var addons = [];
      if (outcome.axle) {
        var rule = config.axle_addons[outcome.axle];
        var total = lines.reduce(function (sum, l) { return sum + l.quantity; }, 0);
        var insert = getProduct(rule.product);
        addons.push({
          type: 'axle_insert',
          key: 'axle_insert',
          product: rule.product,
          variantId: insert.variant_id,
          sku: insert.sku,
          size: rule.size,
          axle: outcome.axle,
          axleLabel: fillVars(rule.axle_label, {}),
          quantity: total
        });
      }
      return { complete: true, outcome: w.outcome, axle: outcome.axle || null, lines: lines, addons: addons };
    }

    // ---- Screen state --------------------------------------------------------------------
    // view: 'parts' | 'question' | 'transition' | 'selection'
    function initialState() {
      return {
        view: 'parts',
        selected: [],      // parts ticked on the part screen, not yet started (never includes completed parts)
        queue: [],         // parts still to go after the current one
        current: null,     // part being answered
        answers: {},       // { part: { questionId: value } } for the part in progress
        drafts: {},        // { questionId: value } for typed fields / grid before Continue
        completed: [],     // finished parts, in the order they were finished
        results: {},       // { part: { answers, quantities } }
        ownInsert: {},     // { part: true } — "I already own the Hexlox Insert"
        ownKey: false,
        hexloxCode: '',
        finished: null     // part just finished (for the "✓ done" screen)
      };
    }

    function currentQuestionId(state) {
      if (state.view !== 'question' || !state.current) return null;
      return walk(state.current, state.answers[state.current]).current;
    }

    function completePart(s) {
      var part = s.current;
      s.results[part] = { answers: s.answers[part], quantities: {} };
      delete s.answers[part];
      if (s.completed.indexOf(part) === -1) s.completed.push(part);
      s.finished = part;
      if (s.queue.length) {
        s.view = 'transition';
      } else {
        s.view = 'selection';
        s.current = null;
      }
      return s;
    }

    function applyAnswer(s, value) {
      var qid = currentQuestionId(s);
      if (!qid) return s;
      var q = getQuestion(qid);
      if (!isValidAnswer(s.current, q, value)) return s;
      s.answers[s.current] = Object.assign({}, s.answers[s.current]);
      s.answers[s.current][qid] = clone(value);
      if (walk(s.current, s.answers[s.current]).outcome) return completePart(s);
      return s;
    }

    function dispatch(state, action) {
      var s = clone(state || initialState());
      action = action || {};
      var qid;
      switch (action.type) {
        case 'restart':
          return initialState();

        case 'togglePart':
          getPart(action.part);
          if (s.view !== 'parts' || s.completed.indexOf(action.part) !== -1) return s;
          if (s.selected.indexOf(action.part) === -1) s.selected.push(action.part);
          else s.selected = s.selected.filter(function (p) { return p !== action.part; });
          return s;

        case 'start':
          s.selected = s.selected.filter(function (p) { return s.completed.indexOf(p) === -1; });
          if (s.view !== 'parts' || !s.selected.length) return s;
          s.current = s.selected[0];
          s.queue = s.selected.slice(1);
          s.selected = [];
          s.answers[s.current] = s.answers[s.current] || {};
          s.view = 'question';
          return s;

        case 'answer':
          // action.question guards against a stale double tap answering the next question.
          qid = currentQuestionId(s);
          if (!qid || (action.question && action.question !== qid)) return s;
          return applyAnswer(s, action.value);

        case 'setDraft':
          // Typed thru-axle field: { field, value } — kept exactly as typed.
          qid = currentQuestionId(s);
          if (!qid || getQuestion(qid).type !== 'measurements') return s;
          s.drafts[qid] = Object.assign({}, s.drafts[qid]);
          s.drafts[qid][action.field] = String(action.value === undefined ? '' : action.value);
          return s;

        case 'gridQty':
          // Other Parts grid: { row, delta }
          qid = currentQuestionId(s);
          if (!qid || getQuestion(qid).type !== 'grid') return s;
          if (!getQuestion(qid).rows.some(function (r) { return r.id === action.row; })) return s;
          s.drafts[qid] = Object.assign({}, s.drafts[qid]);
          s.drafts[qid][action.row] = Math.max(0, toQty(s.drafts[qid][action.row]) + Number(action.delta || 0));
          return s;

        case 'submit':
          // Continue on a typed-fields or grid question: submits the draft as the answer.
          qid = currentQuestionId(s);
          if (!qid) return s;
          return applyAnswer(s, s.drafts[qid] || {});

        case 'back':
          if (s.view !== 'question' || !s.current) return s;
          var visited = walk(s.current, s.answers[s.current]).visited;
          if (visited.length) {
            s.answers[s.current] = Object.assign({}, s.answers[s.current]);
            delete s.answers[s.current][visited[visited.length - 1]];
            return s;
          }
          // Back on the first question: return to the part screen with the unfinished parts still ticked.
          s.selected = [s.current].concat(s.queue).filter(function (p) { return s.completed.indexOf(p) === -1; });
          delete s.answers[s.current];
          s.queue = [];
          s.current = null;
          s.view = 'parts';
          return s;

        case 'continue':
          if (s.view !== 'transition' || !s.queue.length) return s;
          s.current = s.queue.shift();
          s.answers[s.current] = s.answers[s.current] || {};
          s.finished = null;
          s.view = 'question';
          return s;

        case 'changeQty':
        case 'setQty':
          if (!hasOwn(s.results, action.part)) return s;
          var r = resolve(action.part, s.results[action.part].answers, { quantities: s.results[action.part].quantities });
          var line = r.lines.filter(function (l) { return l.key === action.line; })[0];
          if (!line) return s;
          var next = action.type === 'setQty' ? Number(action.quantity) : line.quantity + Number(action.delta || 0);
          s.results[action.part].quantities[action.line] = Math.max(1, Number.isInteger(next) ? next : line.quantity);
          return s;

        case 'ownInsert':
          if (!hasOwn(s.results, action.part)) return s;
          if (action.owned) s.ownInsert[action.part] = true;
          else delete s.ownInsert[action.part];
          return s;

        case 'ownKey':
          s.ownKey = !!action.owned;
          return s;

        case 'hexloxCode':
          s.hexloxCode = String(action.value === undefined ? '' : action.value).slice(0, config.hexlox_code.max_length);
          return s;

        case 'addAnother':
          s.view = 'parts';
          s.selected = [];
          s.queue = [];
          s.current = null;
          s.finished = null;
          return s;

        case 'showSelection':
          if (!s.completed.length) return s;
          s.view = 'selection';
          s.selected = [];
          s.queue = [];
          s.current = null;
          return s;

        default:
          throw new Error('Hexlox Finder: unknown action "' + action.type + '"');
      }
    }

    // ---- Selection and cart --------------------------------------------------------------
    // Finished parts in the Finder's part order, each with its lines and add-ons.
    function selection(state) {
      return partOrder.filter(function (p) { return hasOwn(state.results, p); }).map(function (p) {
        var r = resolve(p, state.results[p].answers, { quantities: state.results[p].quantities });
        r.part = p;
        r.addons = r.addons.map(function (a) { return Object.assign({}, a, { include: !state.ownInsert[p] }); });
        return r;
      });
    }

    function summary(state) {
      var sel = selection(state);
      return {
        parts: sel.length,
        products: sel.reduce(function (n, r) { return n + r.lines.reduce(function (m, l) { return m + l.quantity; }, 0); }, 0)
      };
    }

    function continueCount(state) {
      return state.selected.filter(function (p) { return state.completed.indexOf(p) === -1; }).length;
    }

    // "I already own the Insert" entries, e.g. "Hexlox Insert 6 mm (Front wheel)" or "2 × Hexlox Insert 6 mm (Rear wheel)".
    function ownedInsertEntries(state, partLabel) {
      var entries = [];
      selection(state).forEach(function (r) {
        r.addons.forEach(function (a) {
          if (a.include || a.quantity < 1) return;
          entries.push((a.quantity > 1 ? a.quantity + ' × ' : '') + getProduct(a.product).name + ' (' + partLabel(r.part) + ')');
        });
      });
      return entries;
    }

    // opts.cartHasKey: true when cart.js already contains the Hexlox Key (then it is not added again).
    // opts.cartAttributes: the cart's current attributes (from cart.js), so earlier Insert notes are kept.
    // opts.partLabel(partId): staff-facing part name for the Inserts note (default: the part ID).
    function buildCart(state, opts) {
      opts = opts || {};
      var items = [];
      selection(state).forEach(function (r) {
        r.lines.forEach(function (l) {
          items.push({ id: l.variantId, quantity: l.quantity, properties: Object.assign({}, l.properties, { _finder_part: r.part }) });
        });
        r.addons.forEach(function (a) {
          if (a.include && a.quantity > 0) {
            items.push({ id: a.variantId, quantity: a.quantity, properties: { _finder_part: r.part, _finder_addon: 'insert' } });
          }
        });
      });
      if (items.length && !state.ownKey && !opts.cartHasKey) {
        var key = getProduct(config.key_addon.product);
        items.push({ id: key.variant_id, quantity: config.key_addon.quantity, properties: { _finder_addon: 'key' } });
      }
      var code = String(state.hexloxCode || '').trim();
      var attributes = {};
      if (code) attributes[config.hexlox_code.attribute] = code;
      var notes = config.owned_notes;
      if (notes && items.length) {
        var current = opts.cartAttributes || {};
        // Key: ticked → note; unticked → "" removes a note left by an earlier Finder round.
        if (state.ownKey) attributes[notes.key.attribute] = notes.key.value;
        else if (current[notes.key.attribute]) attributes[notes.key.attribute] = '';
        // Inserts: this round's ticked boxes, added to the entries already on the cart (no duplicates).
        var fresh = ownedInsertEntries(state, opts.partLabel || function (p) { return p; });
        if (fresh.length) {
          var before = String(current[notes.inserts.attribute] || '');
          if (before.indexOf(notes.inserts.prefix) === 0) before = before.slice(notes.inserts.prefix.length);
          var all = before ? before.split('; ') : [];
          fresh.forEach(function (e) { if (all.indexOf(e) === -1) all.push(e); });
          attributes[notes.inserts.attribute] = notes.inserts.prefix + all.join('; ');
        }
      }
      // Hidden order note (leading "_"): marks every order the Finder added to, so Finder orders and revenue
      // can be counted in Shopify without analytics cookies.
      if (items.length) attributes._finder = String(config.finder_version);
      return { items: items, attributes: attributes };
    }

    function cartHasVariant(cart, variantId) {
      return !!(cart && Array.isArray(cart.items) && cart.items.some(function (i) {
        return Number(i.variant_id || i.id) === Number(variantId);
      }));
    }

    return {
      version: config.finder_version,
      parts: partOrder.slice(),
      nextQuestion: nextQuestion,
      path: path,
      resolve: resolve,
      checkMeasurement: checkMeasurement,
      initialState: initialState,
      dispatch: dispatch,
      currentQuestionId: currentQuestionId,
      selection: selection,
      summary: summary,
      continueCount: continueCount,
      buildCart: buildCart,
      keyVariantId: getProduct(config.key_addon.product).variant_id,
      cartHasKey: function (cart) { return cartHasVariant(cart, getProduct(config.key_addon.product).variant_id); }
    };
  }

  return { create: create, parseMeasurement: parseMeasurement };
});
